type UnknownRecord = Record<string, unknown>

const RETRYABLE_MFA_ERROR_TYPES = new Set(['totp_code_already_authenticated'])

export const OAUTH_INVALID_STATE_ERROR_TYPE = 'oauth_invalid_state'
export const OAUTH_INVALID_STATE_USER_MESSAGE =
  'This Google sign-in request expired. Please start again.'

const SESSION_ALREADY_GONE_ERROR_TYPES = new Set(['session_not_found', 'user_unauthenticated'])

export type StytchErrorClassification = {
  isMagicLinkFailure: boolean
  isMfaFailure: boolean
  isOAuthInvalidState: boolean
  errorType: string
  userMessage: string
  shouldResetWidget: boolean
}

/**
 * Extracts common Stytch API error fields regardless of nesting shape.
 */
function extractStytchFields(err: unknown): {
  name?: string
  errorType?: string
  statusCode?: number
  errorMessage?: string
  rawMessage?: string
} {
  if (!err || typeof err !== 'object') {
    return {}
  }

  const anyErr = err as UnknownRecord
  const nestedError = (anyErr['error'] as UnknownRecord) || (anyErr['data'] as UnknownRecord) || {}

  const name = (anyErr['name'] as string) || (nestedError['name'] as string)
  const errorType = (anyErr['error_type'] as string) || (nestedError['error_type'] as string) || ''
  const statusCodeRaw =
    (anyErr['status_code'] as number | string | undefined) ??
    (nestedError['status_code'] as number | string | undefined)
  const statusCode = typeof statusCodeRaw === 'string' ? Number(statusCodeRaw) : statusCodeRaw
  const errorMessage =
    (anyErr['error_message'] as string) || (nestedError['error_message'] as string)
  const rawMessage = (anyErr['message'] as string) || (nestedError['message'] as string)

  return { name, errorType, statusCode, errorMessage, rawMessage }
}

/** True when revoke/logout failed only because Stytch already has no session. */
export function isStytchSessionAlreadyGone(err: unknown): boolean {
  const { errorType } = extractStytchFields(err)
  return SESSION_ALREADY_GONE_ERROR_TYPES.has((errorType || '').toLowerCase())
}

/**
 * Classify a Stytch error for MFA/TOTP failures with robust fallbacks.
 */
export function classifyStytchError(err: unknown): StytchErrorClassification {
  // 1) Prefer structured fields
  const { name, errorType, statusCode, errorMessage, rawMessage } = extractStytchFields(err)
  const isStytch = name === 'StytchAPIError'
  const type = (errorType || '').toLowerCase()
  const isMfaType = type.includes('totp') || type.includes('otp') || type.includes('mfa')
  const is401 = statusCode === 401
  if (type === OAUTH_INVALID_STATE_ERROR_TYPE) {
    return {
      isMagicLinkFailure: false,
      isMfaFailure: false,
      isOAuthInvalidState: true,
      errorType: OAUTH_INVALID_STATE_ERROR_TYPE,
      userMessage: OAUTH_INVALID_STATE_USER_MESSAGE,
      shouldResetWidget: false,
    }
  }
  if (isStytch && isMfaType && is401) {
    return {
      isMagicLinkFailure: false,
      isMfaFailure: true,
      isOAuthInvalidState: false,
      errorType: type,
      userMessage: errorMessage || 'Multi-factor authentication failed. Please try again.',
      shouldResetWidget: false,
    }
  }

  if (isStytch && RETRYABLE_MFA_ERROR_TYPES.has(type)) {
    return {
      isMagicLinkFailure: false,
      isMfaFailure: true,
      isOAuthInvalidState: false,
      errorType: type,
      userMessage: errorMessage || 'That code was already used. Enter the next code and try again.',
      shouldResetWidget: false,
    }
  }

  // 2) Fallback to string heuristics
  const rawFromObj = rawMessage || (typeof err === 'string' ? err : '')
  const serialized = rawFromObj
    ? rawFromObj
    : typeof err === 'string'
      ? err
      : err && typeof err === 'object'
        ? JSON.stringify(err)
        : ''
  const msg = serialized.toLowerCase()

  // Handle stale/expired intermediate session cases which require a widget reset
  if (msg.includes('intermediate session could not be found')) {
    return {
      isMagicLinkFailure: false,
      isMfaFailure: false,
      isOAuthInvalidState: false,
      errorType: type || 'intermediate_session_not_found',
      userMessage: 'Your session expired. Please try again.',
      shouldResetWidget: true,
    }
  }

  // Only combine vague copy ("login link") with this specific error_type to avoid
  // treating unrelated Stytch errors as magic-link failures.
  const unauthorizedCredentialsMagicLinkHint =
    type === 'unauthorized_credentials' &&
    (msg.includes('magic') ||
      msg.includes('magic_links_token') ||
      msg.includes('magic link') ||
      msg.includes('login link'))

  const magicLinkFailure =
    type.includes('magic_link') ||
    type.includes('magic_links') ||
    unauthorizedCredentialsMagicLinkHint
  if (magicLinkFailure) {
    return {
      isMagicLinkFailure: true,
      isMfaFailure: false,
      isOAuthInvalidState: false,
      errorType: type || 'magic_link_failure',
      userMessage:
        errorMessage ||
        'This sign-in link is invalid, expired, or has already been used. Request a new link below.',
      shouldResetWidget: false,
    }
  }

  const mfaIndicator = /(\bmfa\b|\btotp\b|\botp\b|\bcode\b)/.test(msg)
  const failedIndicator = /(unable_to_authenticate_totp|invalid|incorrect|failed|expired|bad)/.test(
    msg,
  )
  if (mfaIndicator && failedIndicator) {
    return {
      isMagicLinkFailure: false,
      isMfaFailure: true,
      isOAuthInvalidState: false,
      errorType: type || 'mfa_failure',
      userMessage: serialized || 'Multi-factor authentication failed. Please try again.',
      shouldResetWidget: false,
    }
  }

  // 3) Generic fallback
  return {
    isMagicLinkFailure: false,
    isMfaFailure: false,
    isOAuthInvalidState: false,
    errorType: type || 'generic',
    userMessage: serialized || 'Authentication failed.',
    shouldResetWidget: false,
  }
}

/**
 * Builds a reusable Stytch onError handler that invokes provided side-effects.
 */
export function createStytchOnError(params: {
  onMagicLinkFailure?: (message: string) => void
  onMfaFailure: (message: string) => void
  onGenericError: (message: string, errorType?: string) => void
  onReset?: (message: string) => void
}): (err: unknown) => void {
  const { onMagicLinkFailure, onMfaFailure, onGenericError, onReset } = params
  return (err: unknown) => {
    const { isMagicLinkFailure, isMfaFailure, userMessage, shouldResetWidget, errorType } =
      classifyStytchError(err)
    if (shouldResetWidget) {
      onReset?.(userMessage)
      return
    }
    if (isMfaFailure) {
      onMfaFailure(userMessage)
      return
    }
    if (isMagicLinkFailure) {
      if (onMagicLinkFailure) {
        onMagicLinkFailure(userMessage)
        return
      }
      onGenericError(userMessage, errorType)
      return
    }
    onGenericError(userMessage, errorType)
  }
}
